/**
 * useVoiceInput — free, browser-native speech-to-text via the Web Speech API.
 *
 * Why not the existing `useAudioRecorder` + `transcribeAudio`?
 *   That path records audio and POSTs it to the backend, which calls
 *   OpenAI Whisper-1 ($0.006/minute). The user's brief for this feature
 *   was explicit: free, no per-minute cost. Web Speech API runs entirely
 *   in the browser (Chrome / Edge / Safari ship on-device or Google-cloud
 *   recognition for free) and supports the two languages we ship: en-US
 *   and he-IL.
 *
 * Design choices in this rev:
 *   - `continuous = true`: a single tap should keep listening for as long
 *     as the user speaks (up to MAX_LISTEN_SECONDS). The Web Speech API's
 *     default behaviour stops on the first ~2s of silence, which felt
 *     like the mic was "cutting out" mid-sentence.
 *   - Parallel mic stream: we open our own `getUserMedia` while
 *     recognition is running so we can drive a real AnalyserNode-based
 *     waveform. The browser's permission prompt fires once and is
 *     shared with Web Speech's internal stream — Chrome/Edge/Safari
 *     handle the dual-tap fine. If the second `getUserMedia` ever
 *     fails the recognition itself still works; the waveform just
 *     sits at zero.
 *   - 60-second hard cap: even with `continuous = true`, the mic
 *     auto-stops at MAX_LISTEN_SECONDS so a forgotten tab can't run
 *     the recogniser indefinitely.
 *
 * Browser support: Chrome desktop, Edge, Safari (mac + iOS 14.5+),
 * Android Chrome. NOT Firefox — callers must check `isSupported` and
 * hide the UI when false.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceInputState = 'idle' | 'listening' | 'error';

/** Hard cap so an idle tab can't leave the mic open forever. */
const MAX_LISTEN_SECONDS = 60;

/** Number of waveform bars the UI renders. Drives the FFT bucket count. */
export const VOICE_BAR_COUNT = 5;

interface UseVoiceInputOptions {
  /** Called with the FINAL transcript when the user finishes speaking. */
  onFinalTranscript: (text: string) => void;
  /** Optional: called with each interim transcript so the UI can show
   *  partial words while the user is still speaking. */
  onInterimTranscript?: (text: string) => void;
  /** Called when recognition errors — typically `'not-allowed'` (user
   *  denied mic permission) or `'no-speech'` (silence timeout). */
  onError?: (errorCode: string) => void;
  /** Locale for the speech recognizer. Pass 'en' or 'he' from
   *  `useLanguage().language`; the hook maps it to the right BCP-47 tag. */
  language: 'en' | 'he';
}

interface UseVoiceInputReturn {
  /** Whether the current browser supports Web Speech API at all. */
  isSupported: boolean;
  /** Current recording state. */
  state: VoiceInputState;
  /** Most recent error code, if any. Cleared when start() is called again. */
  errorCode: string | null;
  /**
   * Per-bar audio levels in [0, 1]. Updated in place every animation
   * frame while listening — read via ref in a child rAF loop and apply
   * to CSS without triggering React re-renders. While idle the array
   * stays at zero.
   */
  levelsRef: React.MutableRefObject<number[]>;
  /** Whole-second elapsed counter while listening. Resets on stop. */
  durationSeconds: number;
  /** Begin listening. No-op if already listening or unsupported. */
  start: () => void;
  /** Stop listening and finalize the current transcript. */
  stop: () => void;
}

function toBcp47(lang: 'en' | 'he'): string {
  return lang === 'he' ? 'he-IL' : 'en-US';
}

function getSpeechRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useVoiceInput({
  onFinalTranscript,
  onInterimTranscript,
  onError,
  language,
}: UseVoiceInputOptions): UseVoiceInputReturn {
  const Ctor = getSpeechRecognitionCtor();
  const isSupported = !!Ctor;

  const [state, setState] = useState<VoiceInputState>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);

  // Live recognition instance.
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Highest `ev.results[]` index that has ALREADY been emitted as
  // final to the caller. Reset to -1 on every `start()`.
  //
  // Why: in `continuous` mode Chrome / Edge sometimes fire onresult
  // with `ev.resultIndex` pointing BACKWARDS at a result we already
  // finalised on a previous event — typically right after a brief
  // silence when the recogniser briefly re-evaluates the last
  // utterance before committing the next one. Without de-dup the
  // caller's `pendingFinals` ends up with the same phrase appended
  // twice ("hello world hello world how are you"). User flagged
  // 2026-06-12: "the mic writes some of the text duplicated". This
  // ref is the authoritative cursor — anything at index <= it has
  // been seen and must not be emitted again.
  const lastEmittedFinalIndexRef = useRef<number>(-1);

  // Audio analyser plumbing. Kept in refs so re-renders don't tear them
  // down — they're created on start, torn down on stop.
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const fftBufferRef = useRef<Uint8Array | null>(null);

  // Per-bar smoothed levels (0..1). The UI reads this ref in its own rAF
  // loop and applies values to CSS — no React re-render per frame.
  const levelsRef = useRef<number[]>(new Array(VOICE_BAR_COUNT).fill(0));

  // Auto-stop / duration timers.
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  // Keep callback refs fresh without re-binding the recognition instance.
  const onFinalRef = useRef(onFinalTranscript);
  const onInterimRef = useRef(onInterimTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => { onFinalRef.current = onFinalTranscript; }, [onFinalTranscript]);
  useEffect(() => { onInterimRef.current = onInterimTranscript; }, [onInterimTranscript]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // ────────────────────────────────────────────────────────────────────
  // Analyser plumbing — opens a parallel mic stream + AnalyserNode
  // alongside Web Speech's internal stream. Permission is shared, so
  // the user only sees one prompt.
  // ────────────────────────────────────────────────────────────────────
  const teardownAnalyser = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (analyserRef.current) {
      try { analyserRef.current.disconnect(); } catch { /* already gone */ }
      analyserRef.current = null;
    }
    if (audioCtxRef.current) {
      // close() returns a promise; we don't need to await it.
      audioCtxRef.current.close().catch(() => { /* already closed */ });
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch { /* ignore */ } });
      streamRef.current = null;
    }
    fftBufferRef.current = null;
    // Decay the visible bars to zero rather than snap, so the listening-
    // → idle transition reads as a graceful settle.
    levelsRef.current = new Array(VOICE_BAR_COUNT).fill(0);
  }, []);

  const setupAnalyser = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Some Safari versions ship `webkitAudioContext` only.
      const Ctx: typeof AudioContext =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;          // 32 bins — plenty for 5 buckets
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;
      fftBufferRef.current = new Uint8Array(analyser.frequencyBinCount);

      const buckets = VOICE_BAR_COUNT;
      // Smoothed per-bar values so frame-to-frame jitter doesn't make
      // bars flicker. Each frame we lerp toward the new measured value.
      const smoothed = new Array(buckets).fill(0);
      const SMOOTH = 0.55;             // weight of previous frame (higher = smoother)
      const GAIN = 1.7;                // boost so quiet speech still moves the bars

      const tick = () => {
        const a = analyserRef.current;
        const buf = fftBufferRef.current;
        if (!a || !buf) return;
        // The lib.dom type wants `Uint8Array<ArrayBuffer>` specifically,
        // but our buf's buffer is typed `ArrayBufferLike`. Cast at the
        // call site — the runtime contract is identical.
        a.getByteFrequencyData(buf as unknown as Uint8Array<ArrayBuffer>);

        // Split the (low-end-biased) frequency range into N buckets and
        // average each bucket. We deliberately weight the lower bins more
        // — voice energy clusters there.
        const binsPerBucket = Math.max(1, Math.floor(buf.length / buckets));
        for (let b = 0; b < buckets; b += 1) {
          let sum = 0;
          const start = b * binsPerBucket;
          const end = Math.min(buf.length, start + binsPerBucket);
          for (let i = start; i < end; i += 1) sum += buf[i];
          const avg = sum / Math.max(1, end - start) / 255;       // 0..1
          const boosted = Math.min(1, avg * GAIN);
          smoothed[b] = SMOOTH * smoothed[b] + (1 - SMOOTH) * boosted;
        }
        levelsRef.current = smoothed.slice();

        rafIdRef.current = requestAnimationFrame(tick);
      };
      rafIdRef.current = requestAnimationFrame(tick);
    } catch {
      // getUserMedia blocked or failed — recognition still works, the
      // bars just stay flat. No error toast: the recognition's own
      // onerror will surface permission denial if it matters.
    }
  }, []);

  // ────────────────────────────────────────────────────────────────────
  // Recognition lifecycle — re-create on language flip, since
  // `r.lang` mid-session isn't reliable across browsers.
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!Ctor) return;
    const r = new Ctor();
    r.continuous = true;             // KEY CHANGE — keep listening on silence
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.lang = toBcp47(language);

    r.onresult = (ev) => {
      // De-dup gate: trust `lastEmittedFinalIndexRef` rather than
      // `ev.resultIndex`. Walk the FULL results list and only emit a
      // final for indices we haven't already seen as final. This
      // works around the Chrome continuous-mode quirk where
      // `ev.resultIndex` can regress to a result that's already
      // been committed (the recogniser briefly re-evaluates the
      // previous utterance before locking the next one — without
      // the gate the caller sees the same phrase twice).
      let interim = '';
      let final = '';
      let newHighFinal = lastEmittedFinalIndexRef.current;
      for (let i = 0; i < ev.results.length; i += 1) {
        const result = ev.results[i];
        const transcript = result[0]?.transcript || '';
        if (result.isFinal) {
          if (i > lastEmittedFinalIndexRef.current) {
            final += transcript;
            if (i > newHighFinal) newHighFinal = i;
          }
          // else: already emitted — skip silently.
        } else {
          // Interim text is ALWAYS recomputed from the live
          // non-final tail; the caller replaces (not appends) on
          // every interim event, so re-walking is the right shape.
          interim += transcript;
        }
      }
      lastEmittedFinalIndexRef.current = newHighFinal;
      if (interim && onInterimRef.current) onInterimRef.current(interim);
      if (final) onFinalRef.current(final);
    };
    r.onerror = (ev) => {
      const code = ev.error || 'unknown';
      // 'no-speech' is benign in continuous mode (just a quiet stretch).
      // We swallow it instead of flipping the UI to error.
      if (code === 'no-speech' || code === 'aborted') return;
      setErrorCode(code);
      setState('error');
      onErrorRef.current?.(code);
    };
    r.onend = () => {
      // The recogniser may end on its own (browser quirk, network
      // hiccup, etc). Tear down analyser + timers so the UI resets.
      setState((s) => (s === 'listening' ? 'idle' : s));
    };

    recognitionRef.current = r;
    return () => {
      try { r.abort(); } catch { /* already stopped */ }
      recognitionRef.current = null;
    };
  }, [Ctor, language]);

  // Side-effect cleanup whenever we leave the listening state — covers
  // both manual stop and onend-driven stop without duplicating logic.
  useEffect(() => {
    if (state !== 'listening') {
      teardownAnalyser();
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      setDurationSeconds(0);
    }
  }, [state, teardownAnalyser]);

  // Component-unmount safety.
  useEffect(() => () => {
    teardownAnalyser();
    if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
  }, [teardownAnalyser]);

  const start = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    if (state === 'listening') return;
    setErrorCode(null);
    setState('listening');
    setDurationSeconds(0);
    startedAtRef.current = Date.now();
    // Fresh session — every previous final has been consumed by the
    // caller and the recogniser's `ev.results[]` index space starts
    // at 0 again. Reset the de-dup cursor so the first new final
    // (which lands at i=0) is actually emitted.
    lastEmittedFinalIndexRef.current = -1;

    // ── Fire recognition FIRST, then everything else. ──
    //
    // The user-facing complaint (2026-06-09): "mic takes a few seconds
    // to turn on". Pre-fix, we called `setupAnalyser()` BEFORE
    // `r.start()`, and although setupAnalyser isn't awaited, it
    // synchronously creates an AudioContext + queues getUserMedia
    // BEFORE the recognition gets a chance to spin up. On a cold
    // start that's ~200-400ms before the recogniser even sees the
    // audio. Flip the order: recognition first (browser starts
    // listening immediately), analyser second (purely visual, can
    // lag without affecting transcription latency).
    try {
      r.start();
    } catch (err: unknown) {
      const code = (err as { name?: string })?.name ?? 'start-failed';
      setErrorCode(code);
      setState('error');
      onErrorRef.current?.(code);
      return;
    }

    // Drive the seconds counter via a 1Hz interval so the timer label
    // ticks predictably without piggybacking on the rAF loop.
    tickIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setDurationSeconds(elapsed);
    }, 250);

    // Hard cap. Same rule as stop(): call r.stop() and let onend drive the
    // idle transition AFTER the trailing final is buffered, so the caller
    // flushes once (no duplicate). Fall back to idle only if stop() throws.
    autoStopTimerRef.current = setTimeout(() => {
      try { recognitionRef.current?.stop(); } catch { setState('idle'); }
    }, MAX_LISTEN_SECONDS * 1000);

    // Audio analyser (parallel stream) — fires async, bars warm up
    // independently. The recogniser is already capturing audio by now.
    setupAnalyser();
  }, [state, setupAnalyser]);

  const stop = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    // Do NOT flip to 'idle' synchronously here. r.stop() makes the
    // recogniser deliver one last FINAL onresult (the in-flight interim,
    // promoted to final) and THEN fire onend. If we set 'idle' right now,
    // the caller flushes its buffered interim into the textarea
    // immediately; when that trailing final lands a moment later it
    // repopulates the caller's buffer and the SAME phrase gets flushed a
    // second time — the "mic duplicates the text after I press stop" bug
    // (2026-06-16). Let `r.onend` be the single 'idle' trigger: it fires
    // only AFTER the trailing final is already buffered, so the caller
    // flushes exactly once. If r.stop() throws (recogniser wasn't
    // running) onend won't come, so fall back to setting idle directly.
    try {
      r.stop();
    } catch {
      setState('idle');
    }
  }, []);

  return {
    isSupported,
    state,
    errorCode,
    levelsRef,
    durationSeconds,
    start,
    stop,
  };
}
